import React, { InputHTMLAttributes, forwardRef } from "react";

interface InputBoxProps extends InputHTMLAttributes<HTMLInputElement> {
  restrictCopyPaste?: boolean;
  onlyNumber?: boolean;
  error?: boolean;
}

const InputBox = forwardRef<HTMLInputElement, InputBoxProps>(
  (
    {
      restrictCopyPaste = false,
      onlyNumber = false,
      error = false,
      className = "",
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (onlyNumber) {
        const allowedKeys = [
          "Backspace",
          "Tab",
          "ArrowLeft",
          "ArrowRight",
          "Delete",
          "End",
        ];
        if (allowedKeys.includes(e.key)) return;

        if (
          (e.ctrlKey || e.metaKey) &&
          ["a", "c", "v", "x"].includes(e.key.toLowerCase())
        ) {
          return;
        }

        if (e.key === ".") {
          const value = (e.target as HTMLInputElement).value;
          if (value.includes(".")) e.preventDefault();
          return;
        }

        if (!/^[0-9]$/.test(e.key)) {
          e.preventDefault();
        }
      }

      if (onKeyDown) onKeyDown(e);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (onlyNumber) {
        const paste = e.clipboardData.getData("text");
        if (!/^[0-9]*\.?[0-9]*$/.test(paste)) {
          e.preventDefault();
        }
      } else if (restrictCopyPaste) {
        e.preventDefault();
      }
    };

    const restrictionProps = restrictCopyPaste
      ? {
        onCopy: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
        onCut: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
        onDragStart: (e: React.DragEvent<HTMLInputElement>) => e.preventDefault(),
        onDrop: (e: React.DragEvent<HTMLInputElement>) => e.preventDefault(),
        onContextMenu: (e: React.MouseEvent<HTMLInputElement>) => e.preventDefault(),
      }
      : {};

    return (
      <input
        {...props}
        {...restrictionProps}
        ref={ref}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        inputMode={onlyNumber ? "decimal" : undefined}
        pattern={onlyNumber ? "[0-9]*[.]?[0-9]*" : undefined}
        className={`border p-1 truncate rounded w-full text-left placeholder:text-left 
          focus:outline-none focus:ring 
          ${error ? "border-red-500 bg-red-50 focus:border-red-500" : "border-gray-300 focus:border-blue-300"}
          ${className}`}
      />
    );
  }
);

InputBox.displayName = "InputBox";
export default InputBox;
